import React, { useCallback, useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { none, equals, map, reject, uniq, concat } from "ramda";
import Grid from "@material-ui/core/Grid";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import DeleteIcon from "@material-ui/icons/Delete";
import ModuleAutoComplete from "../ModuleAutoComplete";
import { useModules, useModuleGroupUpdate } from "../moduleGroupsService";
import { isBlank } from "../../util/ext";

const useStyles = makeStyles(theme => ({
  dialog: {
    borderRadius: "20px",
  },

  form: {
    marginTop: theme.spacing(3),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },

  title: {
    color: "#127DA9",
  },

  errorContainer: {
    marginTop: theme.spacing(1),
    fontWeight: "bold",
    color: theme.palette.error.main,
  },
}));

const AddModuleDialog = props => {
  const { open, onCancel, onOk, initialModuleGroup } = props;
  const classes = useStyles();

  const modules = useModules();

  const [moduleGroup, setModuleGroup] = useState({});
  const [moduleList, setModuleList] = useState([]);
  const [moduleAutoCompleteList, setModuleAutoCompleteList] = useState(modules);
  const [selectedModule, setSelectedModule] = useState({});
  const [disabledModules, setDisabledModules] = useState([]);

  useEffect(() => {
    if (modules.isSuccess) {
      const mappedModules = map(module => {
        return {
          moduleName: module.name,
          moduleId: module.id,
        };
      }, modules.data);

      setModuleAutoCompleteList(mappedModules);
    }
  }, [modules.isSuccess, modules.data]);

  useEffect(() => {
    if (initialModuleGroup.isSuccess) {
      setModuleGroup(initialModuleGroup.data);
      setDisabledModules(initialModuleGroup.data.modules);
    }
  }, [initialModuleGroup.isSuccess, initialModuleGroup.data]);

  const updateModuleGroup = useModuleGroupUpdate();

  const handleApply = useCallback(async () => {
    try {
      const updatedModuleList = uniq(concat(moduleList, moduleGroup.modules));

      moduleGroup.modules = updatedModuleList;
      const updatedModuleGroup = await updateModuleGroup.mutateAsync({
        resourceIds: { id: moduleGroup.id },
        data: moduleGroup,
      });
      setModuleGroup(updatedModuleGroup);
      setModuleList([]);
      onOk();
    } catch (e) {
      console.log(e);
    }
  }, [updateModuleGroup, moduleGroup, moduleList, onOk]);

  const handleClose = useCallback(() => {
    setModuleList([]);
    setDisabledModules(moduleGroup.modules);
    onCancel();
  }, [onCancel, moduleGroup.modules]);

  const handleAddModule = () => {
    if (isBlank(selectedModule)) {
      return;
    }

    if (
      none(module => equals(module.moduleId, selectedModule.moduleId))(
        moduleList,
      )
    ) {
      setModuleList(prevList => [...prevList, selectedModule]);
      setDisabledModules(prevList => [...prevList, selectedModule]);
    }
  };

  const handleRemoveModule = selectedModule => {
    const newModuleList = reject(
      module => equals(module.moduleId, selectedModule.moduleId),
      moduleList,
    );

    setModuleList(newModuleList);
    setDisabledModules(uniq(concat(moduleGroup.modules, newModuleList)));
  };

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={handleClose}
      aria-labelledby="add-module-dialog-title"
      classes={{ root: classes.dialog }}
    >
      <DialogTitle id="add-module-dialog-title" className={classes.title}>
        Add Module
      </DialogTitle>
      <DialogContent>
        <Box className={classes.form}>
          <Grid spacing={2} container>
            <Grid container item xs={12} alignItems="flex-end">
              <Grid item xs={10}>
                <ModuleAutoComplete
                  label="Module"
                  modules={moduleAutoCompleteList}
                  disabledModules={disabledModules}
                  onChange={(event, val) => setSelectedModule(val)}
                />
              </Grid>
              <Grid item xs={2}>
                <Button
                  style={{ padding: "0 6px", float: "right" }}
                  onClick={handleAddModule}
                >
                  Add Module
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell key="module-id">Module ID</TableCell>
                <TableCell key="module-name">Module Name</TableCell>
                <TableCell key="Remove">Remove</TableCell>
              </TableRow>
            </TableHead>
            {moduleList.length > 0 && (
              <TableBody>
                {moduleList.map(module => (
                  <TableRow key={module.moduleId} hover tabIndex={-1}>
                    <TableCell key={"id:" + module.moduleId}>
                      {module.moduleId}
                    </TableCell>
                    <TableCell key={"name:" + module.moduleId}>
                      {module.moduleName}
                    </TableCell>
                    <TableCell key={"remove:" + module.moduleId}>
                      <IconButton onClick={() => handleRemoveModule(module)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Cancel
        </Button>
        <Button
          onClick={handleApply}
          color="primary"
          disabled={moduleList.length === 0 ? true : false}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddModuleDialog;
